import React from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, ReferenceLine,
} from 'recharts';
import { TrendingDown, ShieldCheck, Leaf, Award, CheckCircle2 } from 'lucide-react';

const BLUE = '#7CB8D6';
const YELLOW = '#F4C430';
const LIGHT_BLUE = '#A9C9D9';

// Sample emissions trend (thousands of tons CO2e) trending toward net-zero
const emissionsData = [
  { year: '2020', scope1: 420, scope2: 310, scope3: 980 },
  { year: '2021', scope1: 390, scope2: 285, scope3: 910 },
  { year: '2022', scope1: 350, scope2: 250, scope3: 820 },
  { year: '2023', scope1: 300, scope2: 210, scope3: 700 },
  { year: '2024', scope1: 250, scope2: 170, scope3: 560 },
  { year: '2025', scope1: 190, scope2: 120, scope3: 410 },
];

const breakdownData = [
  { name: 'Manufacturing', value: 42, color: BLUE },
  { name: 'Logistics', value: 28, color: YELLOW },
  { name: 'Facilities', value: 18, color: LIGHT_BLUE },
  { name: 'Other', value: 12, color: '#D5DDE3' },
];

const verifications = [
  { id: '0x7f3a…c21e', label: 'Scope 1 emissions batch', time: '2 min ago' },
  { id: '0x9b12…88af', label: 'Carbon credit retirement', time: '14 min ago' },
  { id: '0x4c8d…19b7', label: 'Renewable energy certificate', time: '38 min ago' },
  { id: '0xa5e0…7d43', label: 'Supplier ESG attestation', time: '1 hr ago' },
];

const kpis = [
  { label: 'CO₂ Tracked', value: '2.4M', unit: 'tons', icon: Leaf, accent: BLUE },
  { label: 'Credits Verified', value: '18,240', unit: 'credits', icon: ShieldCheck, accent: YELLOW },
  { label: 'Net-Zero Progress', value: '68%', unit: 'to target', icon: TrendingDown, accent: BLUE },
  { label: 'Compliance Score', value: '94', unit: '/ 100', icon: Award, accent: YELLOW },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #eef1f4', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ fontSize: 12, color: '#4b5563' }}>
          <span style={{ color: p.color, fontWeight: 700 }}>●</span> {p.name}: {p.value}k t
        </div>
      ))}
    </div>
  );
};

const DashboardMockup = () => {
  return (
    <div className="rounded-3xl bg-white border border-gray-200 shadow-2xl overflow-hidden" data-testid="esg-dashboard-mockup">
      {/* Browser chrome bar */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100" style={{ background: '#F9FAFB' }}>
        <span className="w-3 h-3 rounded-full" style={{ background: '#ff5f57' }} />
        <span className="w-3 h-3 rounded-full" style={{ background: '#febc2e' }} />
        <span className="w-3 h-3 rounded-full" style={{ background: '#28c840' }} />
        <div className="ml-4 flex-1 max-w-md">
          <div className="text-xs text-gray-400 bg-white border border-gray-200 rounded-full px-4 py-1 truncate">
            app.climateco2re.com/dashboard
          </div>
        </div>
        <span className="hidden sm:inline text-xs font-semibold px-3 py-1 rounded-full" style={{ background: 'rgba(40,200,64,0.12)', color: '#1a9e3a' }}>
          ● Live
        </span>
      </div>

      <div className="p-5 md:p-7" style={{ background: '#FBFCFD' }}>
        {/* Header row */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg md:text-xl font-bold" style={{ color: '#1A1A1A' }}>Sustainability Overview</h3>
            <p className="text-xs md:text-sm text-gray-500">Real-time ESG metrics · Blockchain-verified</p>
          </div>
          <div className="hidden md:flex gap-2">
            <span className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500">FY 2025</span>
            <span className="text-xs px-3 py-1.5 rounded-lg text-white" style={{ background: BLUE }}>Export</span>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
          {kpis.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <motion.div
                key={kpi.label}
                className="rounded-2xl bg-white border border-gray-100 p-4"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] md:text-xs text-gray-500 font-medium">{kpi.label}</span>
                  <Icon size={16} style={{ color: kpi.accent }} />
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-xl md:text-2xl font-bold" style={{ color: '#1A1A1A' }}>{kpi.value}</span>
                  <span className="text-[10px] md:text-xs text-gray-400">{kpi.unit}</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Charts row */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Emissions trend area chart */}
          <div className="lg:col-span-2 rounded-2xl bg-white border border-gray-100 p-4 md:p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold" style={{ color: '#1A1A1A' }}>Emissions Trajectory</span>
              <div className="flex gap-3 text-[11px] text-gray-500">
                <span><span style={{ color: BLUE }}>●</span> Scope 1</span>
                <span><span style={{ color: YELLOW }}>●</span> Scope 2</span>
                <span><span style={{ color: LIGHT_BLUE }}>●</span> Scope 3</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={emissionsData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={BLUE} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={YELLOW} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={YELLOW} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={LIGHT_BLUE} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={LIGHT_BLUE} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="scope3" name="Scope 3" stackId="1" stroke={LIGHT_BLUE} fill="url(#g3)" strokeWidth={2} />
                <Area type="monotone" dataKey="scope2" name="Scope 2" stackId="1" stroke={YELLOW} fill="url(#g2)" strokeWidth={2} />
                <Area type="monotone" dataKey="scope1" name="Scope 1" stackId="1" stroke={BLUE} fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown donut + verification feed */}
          <div className="space-y-4">
            <div className="rounded-2xl bg-white border border-gray-100 p-4">
              <span className="text-sm font-bold" style={{ color: '#1A1A1A' }}>Emissions by Source</span>
              <div className="flex items-center gap-2">
                <ResponsiveContainer width="55%" height={120}>
                  <PieChart>
                    <Pie data={breakdownData} dataKey="value" innerRadius={30} outerRadius={50} paddingAngle={2} stroke="none">
                      {breakdownData.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5">
                  {breakdownData.map((d) => (
                    <div key={d.name} className="flex items-center justify-between text-[11px]">
                      <span className="flex items-center gap-1.5 text-gray-600">
                        <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
                        {d.name}
                      </span>
                      <span className="font-semibold text-gray-800">{d.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={15} style={{ color: '#1a9e3a' }} />
                <span className="text-sm font-bold" style={{ color: '#1A1A1A' }}>Blockchain Verified</span>
              </div>
              <div className="space-y-2.5">
                {verifications.map((v) => (
                  <div key={v.id} className="flex items-center gap-2">
                    <CheckCircle2 size={14} style={{ color: '#1a9e3a' }} className="flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-medium text-gray-700 truncate">{v.label}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{v.id} · {v.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMockup;
